import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Check, ArrowLeft, Music, Calendar, Video, Palette, 
  Sparkles, Star, Users, Clock, Target, Award, ChevronRight
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

// Service configurations with commercial content
const serviceDetails = {
  'booking-artistico': {
    icon: Music,
    heroImage: 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg?auto=compress&cs=tinysrgb&w=1200',
    color: '#D4AF37',
    includes: {
      es: [
        'Búsqueda y selección de talento según tu evento',
        'Negociación de contratos y riders técnicos',
        'Coordinación logística completa',
        'Gestión de hospitality y catering de artistas',
        'Supervisión del soundcheck y presentación',
        'Soporte 24/7 durante el evento'
      ],
      en: [
        'Talent search and selection for your event',
        'Contract negotiation and technical riders',
        'Complete logistics coordination',
        'Artist hospitality and catering management',
        'Soundcheck and performance supervision',
        '24/7 support during the event'
      ]
    },
    forWho: {
      es: ['Organizadores de festivales', 'Corporativos', 'Hoteles y resorts', 'Clubs y venues', 'Bodas y eventos privados', 'Marcas'],
      en: ['Festival organizers', 'Corporates', 'Hotels and resorts', 'Clubs and venues', 'Weddings and private events', 'Brands']
    },
    benefits: {
      es: [
        { icon: Users, title: 'Red de +200 artistas', description: 'Acceso exclusivo a talento nacional e internacional' },
        { icon: Clock, title: 'Respuesta en 24h', description: 'Cotizaciones y propuestas en tiempo récord' },
        { icon: Target, title: 'Match perfecto', description: 'Seleccionamos el talento ideal para tu audiencia' },
        { icon: Award, title: '10+ años de experiencia', description: 'Track record comprobado en eventos de alto nivel' }
      ],
      en: [
        { icon: Users, title: 'Network of 200+ artists', description: 'Exclusive access to national and international talent' },
        { icon: Clock, title: '24h response', description: 'Quotes and proposals in record time' },
        { icon: Target, title: 'Perfect match', description: 'We select the ideal talent for your audience' },
        { icon: Award, title: '10+ years experience', description: 'Proven track record in high-level events' }
      ]
    },
    examples: {
      es: ['Festival Nocturno 2024', 'Sunset Sessions Tulum', 'Nike MX Launch'],
      en: ['Night Festival 2024', 'Sunset Sessions Tulum', 'Nike MX Launch']
    }
  },
  'management-artistico': {
    icon: Star,
    heroImage: 'https://images.pexels.com/photos/1190298/pexels-photo-1190298.jpeg?auto=compress&cs=tinysrgb&w=1200',
    color: '#C0C0C0',
    includes: {
      es: [
        'Desarrollo de carrera artística integral',
        'Gestión de imagen y branding personal',
        'Negociación de contratos y patrocinios',
        'Planificación de giras y presentaciones',
        'Relaciones públicas y prensa',
        'Asesoría legal y fiscal'
      ],
      en: [
        'Comprehensive artistic career development',
        'Image management and personal branding',
        'Contract and sponsorship negotiation',
        'Tour and performance planning',
        'Public relations and press',
        'Legal and tax advisory'
      ]
    },
    forWho: {
      es: ['DJs emergentes', 'Bandas en crecimiento', 'Artistas independientes', 'Productores musicales'],
      en: ['Emerging DJs', 'Growing bands', 'Independent artists', 'Music producers']
    },
    benefits: {
      es: [
        { icon: Target, title: 'Estrategia personalizada', description: 'Plan de desarrollo único para tu carrera' },
        { icon: Users, title: 'Conexiones de industria', description: 'Acceso a promotores, sellos y medios' },
        { icon: Award, title: 'Resultados comprobados', description: 'Artistas que han multiplicado sus shows' },
        { icon: Clock, title: 'Dedicación total', description: 'Equipo enfocado en tu crecimiento' }
      ],
      en: [
        { icon: Target, title: 'Personalized strategy', description: 'Unique development plan for your career' },
        { icon: Users, title: 'Industry connections', description: 'Access to promoters, labels and media' },
        { icon: Award, title: 'Proven results', description: 'Artists who have multiplied their shows' },
        { icon: Clock, title: 'Total dedication', description: 'Team focused on your growth' }
      ]
    }
  },
  'produccion-eventos': {
    icon: Calendar,
    heroImage: 'https://images.pexels.com/photos/1540406/pexels-photo-1540406.jpeg?auto=compress&cs=tinysrgb&w=1200',
    color: '#D4AF37',
    includes: {
      es: [
        'Conceptualización y diseño del evento',
        'Selección y gestión de venues',
        'Producción técnica (audio, iluminación, video)',
        'Coordinación de proveedores',
        'Gestión de permisos y seguridad',
        'Dirección de evento el día D'
      ],
      en: [
        'Event conceptualization and design',
        'Venue selection and management',
        'Technical production (audio, lighting, video)',
        'Vendor coordination',
        'Permits and security management',
        'Event direction on D-day'
      ]
    },
    forWho: {
      es: ['Corporativos', 'Marcas', 'Gobiernos', 'ONGs', 'Particulares'],
      en: ['Corporates', 'Brands', 'Governments', 'NGOs', 'Individuals']
    },
    benefits: {
      es: [
        { icon: Award, title: '+500 eventos producidos', description: 'De 50 a 50,000 asistentes' },
        { icon: Users, title: 'Equipo multidisciplinario', description: 'Expertos en cada área de producción' },
        { icon: Target, title: 'Solución integral', description: 'Todo en un solo proveedor' },
        { icon: Clock, title: 'Cumplimiento garantizado', description: 'Entrega en tiempo y forma, siempre' }
      ],
      en: [
        { icon: Award, title: '500+ events produced', description: 'From 50 to 50,000 attendees' },
        { icon: Users, title: 'Multidisciplinary team', description: 'Experts in each production area' },
        { icon: Target, title: 'Comprehensive solution', description: 'Everything in one provider' },
        { icon: Clock, title: 'Guaranteed delivery', description: 'On time and on budget, always' }
      ]
    }
  },
  'produccion-audiovisual': {
    icon: Video,
    heroImage: 'https://images.pexels.com/photos/2774556/pexels-photo-2774556.jpeg?auto=compress&cs=tinysrgb&w=1200',
    color: '#C0C0C0',
    includes: {
      es: [
        'Video promocional y aftermovie',
        'Fotografía profesional de eventos',
        'Streaming en vivo multi-plataforma',
        'Diseño de escenografía y mapping',
        'Contenido para redes sociales',
        'Edición y postproducción'
      ],
      en: [
        'Promotional video and aftermovie',
        'Professional event photography',
        'Multi-platform live streaming',
        'Stage design and mapping',
        'Social media content',
        'Editing and post-production'
      ]
    },
    forWho: {
      es: ['Eventos corporativos', 'Festivales', 'Lanzamientos de marca', 'Conciertos', 'Conferencias'],
      en: ['Corporate events', 'Festivals', 'Brand launches', 'Concerts', 'Conferences']
    },
    benefits: {
      es: [
        { icon: Award, title: 'Equipo cinematográfico', description: 'Producción de calidad cine' },
        { icon: Target, title: 'Storytelling único', description: 'Capturamos la esencia de tu evento' },
        { icon: Clock, title: 'Entrega rápida', description: 'Highlights listos en 48h' },
        { icon: Users, title: 'Cobertura completa', description: 'Múltiples cámaras y ángulos' }
      ],
      en: [
        { icon: Award, title: 'Cinematic team', description: 'Cinema quality production' },
        { icon: Target, title: 'Unique storytelling', description: 'We capture the essence of your event' },
        { icon: Clock, title: 'Fast delivery', description: 'Highlights ready in 48h' },
        { icon: Users, title: 'Complete coverage', description: 'Multiple cameras and angles' }
      ]
    }
  },
  'branding-marketing': {
    icon: Palette,
    heroImage: 'https://images.pexels.com/photos/1587927/pexels-photo-1587927.jpeg?auto=compress&cs=tinysrgb&w=1200',
    color: '#D4AF37',
    includes: {
      es: [
        'Identidad visual de eventos',
        'Estrategia de marketing digital',
        'Gestión de redes sociales',
        'Publicidad y media buying',
        'Relaciones públicas y prensa',
        'Diseño de materiales promocionales'
      ],
      en: [
        'Event visual identity',
        'Digital marketing strategy',
        'Social media management',
        'Advertising and media buying',
        'Public relations and press',
        'Promotional materials design'
      ]
    },
    forWho: {
      es: ['Eventos', 'Artistas', 'Venues', 'Marcas'],
      en: ['Events', 'Artists', 'Venues', 'Brands']
    },
    benefits: {
      es: [
        { icon: Target, title: 'Estrategia data-driven', description: 'Decisiones basadas en métricas' },
        { icon: Users, title: 'Alcance masivo', description: 'Campañas que generan viralidad' },
        { icon: Award, title: 'ROI comprobado', description: 'Inversión que se traduce en ventas' },
        { icon: Clock, title: 'Agilidad', description: 'Adaptación en tiempo real' }
      ],
      en: [
        { icon: Target, title: 'Data-driven strategy', description: 'Decisions based on metrics' },
        { icon: Users, title: 'Massive reach', description: 'Campaigns that go viral' },
        { icon: Award, title: 'Proven ROI', description: 'Investment that translates into sales' },
        { icon: Clock, title: 'Agility', description: 'Real-time adaptation' }
      ]
    }
  },
  'experiencias-exclusivas': {
    icon: Sparkles,
    heroImage: 'https://images.pexels.com/photos/260689/pexels-photo-260689.jpeg?auto=compress&cs=tinysrgb&w=1200',
    color: '#D4AF37',
    includes: {
      es: [
        'Eventos VIP y privados',
        'Experiencias de marca inmersivas',
        'Pop-ups y activaciones',
        'Hospitality de lujo',
        'Cenas y catas exclusivas',
        'Viajes y retiros corporativos'
      ],
      en: [
        'VIP and private events',
        'Immersive brand experiences',
        'Pop-ups and activations',
        'Luxury hospitality',
        'Exclusive dinners and tastings',
        'Corporate trips and retreats'
      ]
    },
    forWho: {
      es: ['Marcas premium', 'Corporativos', 'Celebridades', 'Clientes VIP'],
      en: ['Premium brands', 'Corporates', 'Celebrities', 'VIP clients']
    },
    benefits: {
      es: [
        { icon: Award, title: 'Exclusividad garantizada', description: 'Experiencias únicas e irrepetibles' },
        { icon: Target, title: 'Personalización total', description: 'Cada detalle a tu medida' },
        { icon: Users, title: 'Red de elite', description: 'Acceso a los mejores proveedores' },
        { icon: Clock, title: 'Discreción absoluta', description: 'Confidencialidad total' }
      ],
      en: [
        { icon: Award, title: 'Guaranteed exclusivity', description: 'Unique and unrepeatable experiences' },
        { icon: Target, title: 'Total personalization', description: 'Every detail tailored to you' },
        { icon: Users, title: 'Elite network', description: 'Access to the best providers' },
        { icon: Clock, title: 'Absolute discretion', description: 'Total confidentiality' }
      ]
    }
  }
};

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedProjects, setRelatedProjects] = useState([]);

  const details = serviceDetails[slug] || serviceDetails['booking-artistico'];
  const IconComponent = details.icon;

  useEffect(() => {
    const fetchService = async () => {
      try {
        const response = await axios.get(`${API}/public/services`);
        const found = response.data.items?.find(s => s.slug === slug);
        if (found) setService(found);
        
        // Fetch related projects
        const projectsResponse = await axios.get(`${API}/public/projects?limit=3`);
        setRelatedProjects(projectsResponse.data.items || []);
      } catch (error) {
        console.error('Error fetching service:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchService();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-12 h-12 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-end" data-testid="service-hero">
        <div className="absolute inset-0">
          <img
            src={details.heroImage}
            alt={service?.name?.[lang] || 'Servicio'}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 to-transparent" />
        </div>

        <div className="container-premium relative pb-20 pt-40">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl"
          >
            <motion.div variants={fadeInUp}>
              <Link 
                to="/services" 
                className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-6"
              >
                <ArrowLeft size={16} />
                {lang === 'es' ? 'Todos los Servicios' : 'All Services'}
              </Link>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex items-center gap-4 mb-6">
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: `${details.color}20`, border: `1px solid ${details.color}40` }}
              >
                <IconComponent size={32} style={{ color: details.color }} />
              </div>
            </motion.div>

            <motion.h1 variants={fadeInUp} className="font-display text-5xl md:text-7xl text-white mb-6">
              {service?.name?.[lang] || service?.name?.es}
            </motion.h1>

            <motion.p variants={fadeInUp} className="text-xl text-white/70 leading-relaxed max-w-2xl">
              {service?.full_description?.[lang] || service?.short_description?.[lang] || service?.short_description?.es}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* What's Included */}
      <section className="section-dark py-20" data-testid="service-includes">
        <div className="container-premium">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2 className="font-display text-3xl md:text-4xl text-white mb-4">
                {lang === 'es' ? '¿Qué Incluye?' : "What's Included?"}
              </h2>
              <p className="text-white/50 mb-8">
                {lang === 'es' 
                  ? 'Todo lo que necesitas para un servicio de primer nivel'
                  : 'Everything you need for a top-tier service'
                }
              </p>
              
              <div className="space-y-4">
                {details.includes?.[lang]?.map((item, idx) => (
                  <motion.div 
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${details.color}20` }}>
                      <Check size={16} style={{ color: details.color }} />
                    </div>
                    <span className="text-white/80">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2 className="font-display text-3xl md:text-4xl text-white mb-4">
                {lang === 'es' ? '¿Para Quién?' : 'Who Is It For?'}
              </h2>
              <p className="text-white/50 mb-8">
                {lang === 'es' 
                  ? 'Este servicio es ideal para:'
                  : 'This service is ideal for:'
                }
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {details.forWho?.[lang]?.map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    viewport={{ once: true }}
                    className="glass-dark rounded-xl p-5 text-center hover:border-[#D4AF37]/30 border border-white/5 transition-colors"
                  >
                    <span className="text-white font-medium">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-[#030303]" data-testid="service-benefits">
        <div className="container-premium">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="font-display text-3xl md:text-4xl text-white mb-4">
              {lang === 'es' ? '¿Por Qué Elegirnos?' : 'Why Choose Us?'}
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              {lang === 'es' 
                ? 'Ventajas que nos diferencian en el mercado'
                : 'Advantages that differentiate us in the market'
              }
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {details.benefits?.[lang]?.map((benefit, idx) => {
              const BenefitIcon = benefit.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  className="glass-dark rounded-2xl p-8 text-center hover:border-[#D4AF37]/30 border border-white/5 transition-all group"
                >
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform"
                    style={{ backgroundColor: `${details.color}15` }}
                  >
                    <BenefitIcon size={28} style={{ color: details.color }} />
                  </div>
                  <h3 className="text-white font-display text-lg mb-2">{benefit.title}</h3>
                  <p className="text-white/50 text-sm">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 section-dark" data-testid="service-cta">
        <div className="container-premium">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="glass-dark rounded-3xl p-12 md:p-16 text-center border border-[#D4AF37]/20"
          >
            <h2 className="font-display text-3xl md:text-5xl text-white mb-6">
              {lang === 'es' 
                ? '¿Listo para Empezar?' 
                : 'Ready to Start?'
              }
            </h2>
            <p className="text-white/60 text-lg mb-10 max-w-2xl mx-auto">
              {lang === 'es'
                ? 'Cuéntanos sobre tu proyecto y te contactamos en menos de 24 horas con una propuesta personalizada.'
                : 'Tell us about your project and we will contact you in less than 24 hours with a personalized proposal.'
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact" 
                className="btn-gold inline-flex items-center justify-center gap-2 text-lg px-10 py-4"
                data-testid="service-contact-btn"
              >
                {lang === 'es' ? 'Solicitar Cotización' : 'Request Quote'}
                <ArrowRight size={20} />
              </Link>
              <a 
                href="https://wa.me/525512345678" 
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary inline-flex items-center justify-center gap-2 text-lg px-10 py-4"
              >
                WhatsApp Directo
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <section className="py-20 bg-[#030303]" data-testid="related-projects">
          <div className="container-premium">
            <div className="flex items-center justify-between mb-12">
              <h2 className="font-display text-3xl text-white">
                {lang === 'es' ? 'Proyectos Relacionados' : 'Related Projects'}
              </h2>
              <Link to="/portfolio" className="text-[#D4AF37] hover:text-white transition-colors flex items-center gap-2">
                {lang === 'es' ? 'Ver todos' : 'View all'}
                <ChevronRight size={16} />
              </Link>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedProjects.map((project, idx) => (
                <Link
                  key={project.project_id || idx}
                  to={`/portfolio/${project.slug}`}
                  className="group relative aspect-[4/3] overflow-hidden rounded-2xl"
                >
                  <img
                    src={project.cover_image || 'https://images.pexels.com/photos/1540406/pexels-photo-1540406.jpeg?auto=compress&cs=tinysrgb&w=600'}
                    alt={project.title?.[lang] || project.title?.es}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <span className="text-[#D4AF37] text-xs uppercase tracking-wider">{project.category}</span>
                    <h3 className="text-white font-display text-xl mt-1 group-hover:text-[#D4AF37] transition-colors">
                      {project.title?.[lang] || project.title?.es}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}
